import { createModel } from '@rematch/core';
import type { RootModel } from '.';
import {
	addDays,
	generateRestAPI,
	getTimezoneOffsetString,
	localToUtc,
	todayInTimezone,
} from '../helpers/helpers';
import { getData as rawGetData } from '../services/commonService';
import {
	IOverviewVehicleAlerts,
	initialValues,
} from '../type/overview-types';

// ─────────────────────────────────────────────────────────────────────────────
// TIMEZONE HANDLING
// Every effect below receives LOCAL dates (user's preferred timezone, from
// vc_user_preferences / Settings) and ONLY shifts startdate / enddate to UTC
// here, in one place, before building the URL. No extra params are sent.
//
//   IST user picks Oct 20 → startdate=2026-10-19T18:30:00 enddate=2026-10-20T18:29:59
// ─────────────────────────────────────────────────────────────────────────────
const getPreferedTz = (rootState: any): string => rootState?.auth?.preferedTimeZone || '';

/** LOCAL {startDate,endDate} → [{startdate},{enddate}] shifted to UTC */
const buildDateParams = (date: { startDate: string; endDate: string }, rootState: any): any[] => {
	const tz = getPreferedTz(rootState);
	return [
		{ startdate: localToUtc(date.startDate, tz) },
		{ enddate: localToUtc(date.endDate, tz) },
	];
};

// ─────────────────────────────────────────────────────────────────────────────
// LOADING FIX — only ONE fetchChartApis run is "active" at a time.
// When the date range or timezone changes while an older run is still going:
//   • the old run's queued (not yet started) API calls are SKIPPED, so the
//     backend no longer gets every API twice (that is what made it so slow);
//   • the old run's in-flight responses are IGNORED when they come back, so
//     they can never overwrite the charts of the new range.
// ─────────────────────────────────────────────────────────────────────────────
let activeRunId = 0;      // id of the newest chart run
let activeKpiTz = '';     // tz offset of the newest KPI tile fetch

type IsStale = () => boolean;
interface IOverviewTask {
	run: () => Promise<unknown>;
	isStale?: IsStale; // undefined → always runs (one-time, non-date APIs)
}

/** True when this response belongs to an older run → don't write it to Redux */
const isStalePayload = (payload: any): boolean =>
	typeof payload?.isStale === 'function' && payload.isStale();

// Several overview endpoints share a backend resource that only handles
// a handful of concurrent requests well (~1.2-1.6s solo, 13-15s each when ~20
// hit it at once), so a fixed number run at the same time.
async function runWithConcurrencyLimit(tasks: IOverviewTask[], limit: number): Promise<void> {
	let cursor = 0;
	const worker = async () => {
		while (cursor < tasks.length) {
			const task = tasks[cursor++];
			if (task.isStale?.()) continue; // newer run started → skip, don't call the API
			try {
				await task.run();
			} catch (err) {
				// eslint-disable-next-line no-console
				console.error('[overview] chart API failed:', err);
			}
		}
	};
	const workerCount = Math.min(limit, tasks.length);
	await Promise.all(Array.from({ length: workerCount }, () => worker()));
}

// ─────────────────────────────────────────────────────────────────────────────
// REQUEST LIMITER — every overview API call goes through this, so the backend
// never gets more than MAX_PARALLEL_REQUESTS at the same time (it slows down a
// lot under load). Extra calls wait in a queue.
// ─────────────────────────────────────────────────────────────────────────────
const MAX_PARALLEL_REQUESTS = 6;
let activeRequests = 0;
const waitQueue: Array<() => void> = [];

async function limited<T>(fn: () => Promise<T>): Promise<T> {
	if (activeRequests < MAX_PARALLEL_REQUESTS) {
		activeRequests += 1;
	} else {
		await new Promise<void>((resolve) => waitQueue.push(resolve)); // slot is handed over
	}
	try {
		return await fn();
	} finally {
		const next = waitQueue.shift();
		if (next) next(); // hand the slot to the next waiting call
		else activeRequests -= 1;
	}
}

const getData = <T = any>(payload: string, type: string, emptyQuery = false): Promise<T> =>
	limited(() => rawGetData<T>(payload, type, emptyQuery));

// ─────────────────────────────────────────────────────────────────────────────
// DAY-WISE CHARTS IN THE USER'S TIMEZONE (frontend only, backend unchanged)
//
// The backend groups day-wise charts by UTC day ($dateToString without a
// timezone). For IST that puts trips made between 00:00 and 05:30 IST into the
// previous day. The frontend cannot split an already-summed day, so it asks
// for ONE IST DAY PER REQUEST and adds up whatever the backend returns:
//
//   IST Oct 20 → startdate=2026-10-19T18:30:00 enddate=2026-10-20T18:29:59
//              → backend may return { Oct 19: a, Oct 20: b } → Oct 20 = a + b
//
// Cost: a range of N days = N requests per day-wise chart (finished days are
// cached, so going back to a range is instant). Single-day ranges need no
// extra request. When the backend adds  timezone: '+05:30'  to $dateToString,
// set REGROUP_BY_LOCAL_DAY = false to go back to one request per chart.
// ─────────────────────────────────────────────────────────────────────────────
const REGROUP_BY_LOCAL_DAY = true;
const MAX_REGROUP_DAYS = 62; // safety cap

type DayValue = number | Record<string, number> | null;
interface IDayPoint { day: string; value: DayValue }

/** finished (past) days only — today keeps changing, so it is never cached */
const dayCache = new Map<string, DayValue>();

const round2 = (n: number) => Math.round(n * 100) / 100;

/** "2026-10-20T00:00:00" … "2026-10-22T23:59:59" → ["2026-10-20","2026-10-21","2026-10-22"] */
const listLocalDays = (startLocal: string, endLocal: string): string[] => {
	const days: string[] = [];
	const last = endLocal.slice(0, 10);
	let d = startLocal.slice(0, 10);
	while (d <= last && days.length < MAX_REGROUP_DAYS) {
		days.push(d);
		d = addDays(d, 1);
	}
	return days;
};

/** "2026-10-20" → "20/10/2026" (format used by the fuel charts) */
const toDMY = (ymd: string) => `${ymd.slice(8, 10)}/${ymd.slice(5, 7)}/${ymd.slice(0, 4)}`;

// ── Extractors: whole API response → ONE value for the requested day ────────
const sumXY = (arr: any): DayValue => {
	if (!Array.isArray(arr) || arr.length === 0) return null;
	return round2(arr.reduce((sum: number, d: any) => sum + (Number(d?.y) || 0), 0));
};
const sumPairs = (arr: any): DayValue => {
	if (!Array.isArray(arr) || arr.length === 0) return null;
	return round2(arr.reduce((sum: number, d: any) => sum + (Number(d?.[1]) || 0), 0));
};
const sumSeries = (arr: any): DayValue => {
	if (!Array.isArray(arr) || arr.length === 0) return null;
	const totals: Record<string, number> = {};
	arr.forEach((dayRow: any) => (dayRow?.series ?? []).forEach((s: any) => {
		totals[s.category] = (totals[s.category] ?? 0) + (Number(s.count) || 0);
	}));
	return Object.keys(totals).length ? totals : null;
};

interface ILoadDayWiseOptions {
	date: any; // LOCAL { startDate, endDate, isStale? }
	rootState: any;
	envUrl?: string;
	label: string;
	extract: (res: any) => DayValue;
	needFullResponse: boolean; // true when the response also has totals (idle, trips, stop)
	extraParams?: any[];
}

/**
 * Returns the full-range response (if needed) and one value per LOCAL day.
 * Returns 'stale' when a newer date range / timezone was selected meanwhile.
 */
async function loadDayWise(opts: ILoadDayWiseOptions): Promise<
	{ full: any[] | null; perDay: IDayPoint[] | null } | 'stale'
> {
	const { date, rootState, envUrl, label, extract, needFullResponse, extraParams = [] } = opts;
	const tz = getPreferedTz(rootState);
	const days = listLocalDays(date.startDate, date.endDate);
	const multiDay = REGROUP_BY_LOCAL_DAY && days.length > 1;

	// 1) Full-range call — for totals, for single-day ranges, or when regrouping is off
	let full: any[] | null = null;
	if (!multiDay || needFullResponse) {
		full = await getData(generateRestAPI([...buildDateParams(date, rootState), ...extraParams], envUrl), label);
		if (isStalePayload(date)) return 'stale';
	}
	if (!REGROUP_BY_LOCAL_DAY) return { full, perDay: null };

	// 2) Single day → every bucket in the response belongs to that local day
	if (!multiDay) return { full, perDay: days.length ? [{ day: days[0], value: extract(full) }] : [] };

	// 3) Several days → one request per local day, summed
	const today = todayInTimezone(tz);
	const { vin, fleetId, organisationId } = rootState?.auth?.customProperties ?? {};
	const perDay = await Promise.all(days.map(async (day, i): Promise<IDayPoint | null> => {
		const localStart = i === 0 ? date.startDate : `${day}T00:00:00`;
		const localEnd = i === days.length - 1 ? date.endDate : `${day}T23:59:59`;
		const url = generateRestAPI(
			[
				{ startdate: localToUtc(localStart, tz) },
				{ enddate: localToUtc(localEnd, tz) },
				...extraParams,
			],
			envUrl,
		);
		const cacheKey = `${url}|${vin}|${fleetId}|${organisationId}`;
		if (dayCache.has(cacheKey)) return { day, value: dayCache.get(cacheKey) ?? null };
		if (isStalePayload(date)) return null; // newer selection → don't call

		const res = await getData(url, `${label} (${day})`);
		const value = extract(res);
		// cache finished days that returned data (empty may be an error → retry next time)
		if (day < today && value !== null) dayCache.set(cacheKey, value);
		return { day, value };
	}));
	if (isStalePayload(date)) return 'stale';

	return { full, perDay: perDay.filter((p): p is IDayPoint => p !== null) };
}

/** Per-day values → [{ x: day, y }] (days without data are left out, like the backend does) */
const toXYData = (perDay: IDayPoint[]) =>
	perDay.filter((p) => typeof p.value === 'number').map((p) => ({ x: p.day, y: p.value as number }));

const toPairData = (perDay: IDayPoint[]): [string, number][] =>
	perDay.filter((p) => typeof p.value === 'number').map((p) => [toDMY(p.day), p.value as number]);

const toSeriesData = (perDay: IDayPoint[]) =>
	perDay
		.filter((p) => p.value && typeof p.value === 'object')
		.map((p) => ({
			x: p.day,
			series: Object.entries(p.value as Record<string, number>).map(([category, count]) => ({ category, count })),
		}));

export const overview = createModel<RootModel>()({
	state: {
		...initialValues,
		// Cache key for chart APIs — "<start>_<end>_<tzOffset>", survives tab navigation
		lastFetchedKey: '' as string,
		// Timezone offset ("+05:30") the KPI tiles were last fetched for.
		// Tiles re-fetch only when this changes (first load or timezone change in Settings).
		kpiFetchedTz: '' as string,
	},
	reducers: {
		setActiveVehicles(state, res) {
			return { ...state, activeVehicles: res };
		},
		setActiveAlertsCount(state, res) {
			return { ...state, activeAlertsCount: res };
		},
		setAvgFuelEfficiencyCount(state, res) {
			return { ...state, avgFuelEfficiencyCount: res };
		},
		setFleetUtilizationPercent(state, res) {
			return { ...state, fleetUtilizationPercent: res };
		},
		setDistanceCovered(state, res) {
			return { ...state, distanceCovered: res };
		},
		setTripDistanceKm(state, res) { return { ...state, tripDistanceKm: res }; },
		setFleetUtilizationRate(state, res) { return { ...state, fleetUtilizationRate: res }; },
		setIdleTimeMin(state, res) { return { ...state, idleTimeMin: res }; },
		setStopDuration(state, res) { return { ...state, stopDuration: res }; },
		setNoOfTrips(state, res) { return { ...state, noOfTrips: res }; },
		setVehicleAlertsV2(state, res) { return { ...state, vehicleAlertsV2: res }; },
		setDistanceDrivenKm(state, res) { return { ...state, distanceDrivenKm: res }; },
		setVehicleTypeUtilization(state, res) {
			return { ...state, vehicleTypeUtilization: res };
		},
		setFuelEnergyTiles(state, res) { return { ...state, fuelEnergyTiles: res }; },
		setFuelConsumptionChart(state, res) { return { ...state, fuelConsumptionChart: res }; },
		setIdleFuelWasteChart(state, res) { return { ...state, idleFuelWasteChart: res }; },
		setDriverScores(state, res) { return { ...state, driverScores: res }; },
		setEcoDriverScore(state, res) { return { ...state, ecoDriverScore: res }; },
		setDriveVsIdle(state, res) { return { ...state, driveVsIdle: res }; },
		setDriveVsIdleAllDrivers(state, res) { return { ...state, driveVsIdleAllDrivers: res }; },
		setDriverNameFilter(state, res) { return { ...state, driverNameFilter: res }; },
		setDashcamAlertEvents(state, res) { return { ...state, dashcamAlertEvents: res }; },
		setObdAlertEvents(state, res) { return { ...state, obdAlertEvents: res }; },
		setDashcamAlertSeverity(state, res) { return { ...state, dashcamAlertSeverity: res }; },
		setMaintenanceStatusV2(state, res) { return { ...state, maintenanceStatusV2: res }; },
		setMaintServiceOverdue(state, res) { return { ...state, maintServiceOverdue: res }; },
		setDateRangeFilterPrev(state, res) {
			return { ...state, dateRangeFilterFromStorePrev: res };
		},
		setOverviewVehicleAlerts(state, res) {
			return { ...state, overviewVehicleAlerts: res };
		},
		setTemperature(state, res) {
			return { ...state, temperature: res };
		},
		// Chart API cache key — cleared when date picker changes
		setLastFetchedKey(state, key: string) {
			return { ...state, lastFetchedKey: key };
		},
		setKpiFetchedTz(state, tzOffset: string) {
			return { ...state, kpiFetchedTz: tzOffset };
		},
		clearStore() {
			return { ...initialValues, lastFetchedKey: '', kpiFetchedTz: '' };
		},
	},

	effects: (dispatch) => ({

		// ─────────────────────────────────────────────────────────────────────
		// syncUserTimezone — re-reads vc_user_preferences so a timezone changed
		// in Settings (or on another device) is picked up when Overview opens.
		// Only updates Redux when the backend returns a real, different value,
		// so a failed request never overwrites a good timezone.
		// ─────────────────────────────────────────────────────────────────────
		async syncUserTimezone(_: void, rootState: any) {
			const userId = rootState?.auth?.user?.user?.id;
			if (!userId) return null;
			const payload = generateRestAPI([{ userid: userId }], process.env.REACT_APP_LANGUAGE);
			const res: any = await getData(payload, "user's language and timezone", true);
			const timezone = Array.isArray(res) ? res[0]?.timezone : undefined;
			if (timezone && timezone !== rootState.auth.preferedTimeZone) {
				dispatch.auth.setTimeZone(timezone);
			}
			return timezone ?? null;
		},

		// ─────────────────────────────────────────────────────────────────────
		// fetchChartApis — called on date picker change AND timezone change.
		// payload = LOCAL dates in the user's timezone, e.g.
		//   { startDate: '2026-10-20T00:00:00', endDate: '2026-10-20T23:59:59' }
		// KPI tiles are fetched on first load and again whenever the timezone
		// changes (their "yesterday → today" window depends on it).
		// ─────────────────────────────────────────────────────────────────────
		async fetchChartApis(
			payload: { startDate: string; endDate: string },
			rootState: any,
		) {
			const tz = getPreferedTz(rootState);
			const tzOffset = getTimezoneOffsetString(tz);
			const cacheKey = `${payload.startDate}_${payload.endDate}_${tzOffset}`;

			// Skip if this exact date range + timezone was already fetched (tab return guard)
			if (rootState.overview.lastFetchedKey === cacheKey) return;
			dispatch.overview.setLastFetchedKey(cacheKey);

			// KPI tile window: yesterday 00:00 → today 00:00 in the USER timezone
			const today = todayInTimezone(tz);
			const kpiDate = {
				startDate: `${addDays(today, -1)}T00:00:00`,
				endDate: `${today}T00:00:00`,
			};

			// Tiles load on first visit and after a timezone change
			const shouldFetchKpi = rootState.overview.kpiFetchedTz !== tzOffset;
			const isFirstLoad = rootState.overview.kpiFetchedTz === '';
			if (shouldFetchKpi) {
				dispatch.overview.setKpiFetchedTz(tzOffset);
				activeKpiTz = tzOffset;
			}

			// This run becomes the active one; any older run is now stale
			const runId = ++activeRunId;
			const chartStale: IsStale = () => runId !== activeRunId;
			const kpiStale: IsStale = () => tzOffset !== activeKpiTz;

			const chart = { ...payload, isStale: chartStale };
			const kpi = { ...kpiDate, isStale: kpiStale };
			const c = (run: () => Promise<unknown>): IOverviewTask => ({ run, isStale: chartStale });
			const k = (run: () => Promise<unknown>): IOverviewTask => ({ run, isStale: kpiStale });
			const once = (run: () => Promise<unknown>): IOverviewTask => ({ run });

			const tasks: IOverviewTask[] = [
				// ── KPI tiles (top row)
				...(shouldFetchKpi ? [
					k(() => dispatch.overview.getActiveVehicles(kpi)),
					k(() => dispatch.overview.getActiveAlertsCount(kpi)),
					k(() => dispatch.overview.getFleetUtilizationPercent(kpi)),
					k(() => dispatch.overview.getDistanceCovered(kpi)),
					k(() => dispatch.overview.getAvgFuelEfficiencyCount(kpi)),
				] : []),

				// ── Section 1 — Location & Movement
				c(() => dispatch.overview.getTripDistanceKm(chart)),
				c(() => dispatch.overview.getFleetUtilizationRate(chart)),
				...(isFirstLoad ? [once(() => dispatch.overview.getVehicleTypeUtilization())] : []),
				c(() => dispatch.overview.getIdleTimeMin(chart)),
				c(() => dispatch.overview.getStopDuration(chart)),
				c(() => dispatch.overview.getVehicleAlertsV2(chart)),
				c(() => dispatch.overview.getNoOfTrips(chart)),

				// ── Section 2 — Fuel & Energy
				c(() => dispatch.overview.getDistanceDrivenKm(chart)),
				c(() => dispatch.overview.getFuelConsumptionChart(chart)),
				c(() => dispatch.overview.getIdleFuelWasteChart(chart)),

				// ── Section 3 — Driver Behaviour
				c(() => dispatch.overview.getDriverScores(chart)),
				c(() => dispatch.overview.getEcoDriverScore(chart)),
				c(() => dispatch.overview.getDriveVsIdle(chart)),

				// ── Section 4 — Safety & Alerts
				c(() => dispatch.overview.getDashcamAlertEvents(chart)),
				c(() => dispatch.overview.getObdAlertEvents(chart)),
				c(() => dispatch.overview.getDashcamAlertSeverity(chart)),

				// ── Section 5 — Maintenance (not date-filtered → once per load)
				...(isFirstLoad ? [
					once(() => dispatch.overview.getMaintenanceStatusV2()),
					once(() => dispatch.overview.getMaintServiceOverdue(chart)),
				] : []),

				// Driver name filter — dropdown only, goes last
				...(isFirstLoad ? [once(() => dispatch.overview.getDriverNameFilter())] : []),
			];

			const OVERVIEW_FETCH_CONCURRENCY = 6;
			await runWithConcurrencyLimit(tasks, OVERVIEW_FETCH_CONCURRENCY);
		},

		// ─────────────────────────────────────────────────────────────────────
		// Individual effects — all take LOCAL dates and convert to UTC inside.
		// ─────────────────────────────────────────────────────────────────────

		async getOverviewVehiclesAlerts(payload, rootState: any) {
			// eslint-disable-next-line @typescript-eslint/naming-convention
			const { alert_type, sortorder } = payload;
			const payloadAlerts = generateRestAPI(
				[{ alert_type, sortorder }, ...buildDateParams(payload.date, rootState)],
				process.env.REACT_APP_OVERVIEW_VEHICLE_ALERTS,
			);
			const getOverviewVehiclesAlertsResponse: IOverviewVehicleAlerts[] = await getData(
				payloadAlerts,
				'overview vehicles alerts',
			);
			if (Array.isArray(getOverviewVehiclesAlertsResponse)) {
				this.setOverviewVehicleAlerts(getOverviewVehiclesAlertsResponse);
			} else {
				this.setOverviewVehicleAlerts([]);
			}
			return getOverviewVehiclesAlertsResponse;
		},

		async getActiveVehicles(date: any, rootState: any) {
			const payload = generateRestAPI(
				buildDateParams(date, rootState),
				process.env.REACT_APP_OVERVIEW_ACTIVE_VEHICLES,
			);
			const res: any[] = await getData(payload, 'Active Vehicles');
			if (isStalePayload(date)) return res; // older run → ignore
			if (Array.isArray(res) && res.length > 0) {
				this.setActiveVehicles(res[0]);
			} else {
				this.setActiveVehicles(initialValues.activeVehicles);
			}
			return res;
		},

		async getActiveAlertsCount(date: any, rootState: any) {
			const payload = generateRestAPI(
				buildDateParams(date, rootState),
				process.env.REACT_APP_ACTIVE_ALERTS_COUNT,
			);
			const res: any[] = await getData(payload, 'Active Alerts Count');
			if (isStalePayload(date)) return res; // older run → ignore
			if (Array.isArray(res) && res.length > 0) {
				this.setActiveAlertsCount(res[0]);
			} else {
				this.setActiveAlertsCount(initialValues.activeAlertsCount);
			}
			return res;
		},

		async getAvgFuelEfficiencyCount(date: any, rootState: any) {
			// Same fallback pattern used elsewhere in the app (see
			// src/services/dashcamService.js) when no org is set on the user.
			const organisationId = rootState?.auth?.customProperties?.organisationId || '1,2';
			const payload = generateRestAPI(
				[
					...buildDateParams(date, rootState),
					{ organisation_id: organisationId },
				],
				process.env.REACT_APP_OVERVIEW_FUEL_TOTAL_EFFICIENCY_COUNT,
			);
			const res: any[] = await getData(payload, 'Avg Fuel Efficiency Count');
			if (isStalePayload(date)) return res; // older run → ignore
			if (Array.isArray(res) && res.length > 0) {
				this.setAvgFuelEfficiencyCount(res[0]);
			} else {
				this.setAvgFuelEfficiencyCount(initialValues.avgFuelEfficiencyCount);
			}
			return res;
		},

		async getFleetUtilizationPercent(date: any, rootState: any) {
			const payload = generateRestAPI(
				buildDateParams(date, rootState),
				process.env.REACT_APP_OVERVIEW_FLEET_UTILIZATION,
			);
			const res: any[] = await getData(payload, 'Fleet Utilization Percent');
			if (isStalePayload(date)) return res; // older run → ignore
			if (Array.isArray(res) && res.length > 0) {
				this.setFleetUtilizationPercent(res[0]);
			} else {
				this.setFleetUtilizationPercent(initialValues.fleetUtilizationPercent);
			}
			return res;
		},

		async getDistanceCovered(date: any, rootState: any) {
			const payload = generateRestAPI(
				buildDateParams(date, rootState),
				process.env.REACT_APP_DISTANCE_DRIVEN,
			);
			const res: any[] = await getData(payload, 'Distance Covered');
			if (isStalePayload(date)) return res; // older run → ignore
			if (Array.isArray(res) && res.length > 0) {
				this.setDistanceCovered(res[0]);
			} else {
				this.setDistanceCovered(null);
			}
			return res;
		},

		async getTripDistanceKm(date: any, rootState: any) {
			const r = await loadDayWise({
				date, rootState, label: 'Trip Distance Km',
				envUrl: process.env.REACT_APP_OVERVIEW_TRIP_DISTANCE_KM,
				extract: (res) => sumXY(res?.[0]?.data),
				needFullResponse: false,
			});
			if (r === 'stale') return null; // older run → ignore
			if (!r.perDay) { // regrouping off → backend response as-is
				this.setTripDistanceKm(Array.isArray(r.full) && r.full.length ? r.full[0] : initialValues.tripDistanceKm);
				return r.full;
			}
			const data = toXYData(r.perDay);
			this.setTripDistanceKm(data.length ? { name: r.full?.[0]?.name ?? 'Trip Distance Km', data } : initialValues.tripDistanceKm);
			return data;
		},

		async getFleetUtilizationRate(date: any, rootState: any) {
			const payload = generateRestAPI(
				buildDateParams(date, rootState),
				process.env.REACT_APP_OVERVIEW_FLEET_UTILIZATION_RATE,
			);
			const res: any[] = await getData(payload, 'Fleet Utilization Rate');
			if (isStalePayload(date)) return res; // older run → ignore
			if (Array.isArray(res) && res.length > 0) { this.setFleetUtilizationRate(res[0]); }
			else { this.setFleetUtilizationRate(initialValues.fleetUtilizationRate); }
			return res;
		},

		async getIdleTimeMin(date: any, rootState: any) {
			const r = await loadDayWise({
				date, rootState, label: 'Idle Time Min',
				envUrl: process.env.REACT_APP_OVERVIEW_IDLE_TIME_HRS,
				extract: (res) => sumXY(res?.[0]?.chart?.data),
				needFullResponse: true, // total_idle / avg_idle_per_vehicle come from the full range
			});
			if (r === 'stale') return null; // older run → ignore
			const base = Array.isArray(r.full) && r.full.length ? r.full[0] : null;
			if (!base) { this.setIdleTimeMin(initialValues.idleTimeMin); return r.full; }
			this.setIdleTimeMin(r.perDay ? { ...base, chart: { ...(base.chart ?? {}), data: toXYData(r.perDay) } } : base);
			return r.full;
		},

		async getStopDuration(date: any, rootState: any) {
			const r = await loadDayWise({
				date, rootState, label: 'Stop Duration',
				envUrl: process.env.REACT_APP_OVERVIEW_STOP_DURATION,
				extract: (res) => sumXY(res?.[0]?.chart),
				needFullResponse: true, // total_stop_duration_hrs / avg_stop_per_vin_hrs
			});
			if (r === 'stale') return null; // older run → ignore
			const base = Array.isArray(r.full) && r.full.length ? r.full[0] : null;
			if (!base) { this.setStopDuration(initialValues.stopDuration); return r.full; }
			this.setStopDuration(r.perDay ? { ...base, chart: toXYData(r.perDay) } : base);
			return r.full;
		},

		async getNoOfTrips(date: any, rootState: any) {
			const tz = getPreferedTz(rootState);
			// "Today" = today in the USER timezone, sent as UTC
			const today = todayInTimezone(tz);
			const r = await loadDayWise({
				date, rootState, label: 'No of Trips',
				envUrl: process.env.REACT_APP_OVERVIEW_NO_OF_TRIPS,
				extract: (res) => sumXY(res?.[0]?.chart?.data),
				needFullResponse: true, // total_trips / avg_per_day
				extraParams: [
					{ todaystartdate: localToUtc(`${today}T00:00:00`, tz) },
					{ todayenddate: localToUtc(`${today}T23:59:59`, tz) },
				],
			});
			if (r === 'stale') return null; // older run → ignore
			const base = Array.isArray(r.full) && r.full.length ? r.full[0] : null;
			if (!base) { this.setNoOfTrips(initialValues.noOfTrips); return r.full; }
			this.setNoOfTrips(r.perDay ? { ...base, chart: { ...(base.chart ?? {}), data: toXYData(r.perDay) } } : base);
			return r.full;
		},

		async getVehicleAlertsV2(date: any, rootState: any) {
			const payload = generateRestAPI(
				[
					...buildDateParams(date, rootState),
					{ alert_type: 'All Alerts' },
					{ sortorder: -1 },
				],
				process.env.REACT_APP_OVERVIEW_VEHICLE_ALERTS_V2,
			);
			const res: any[] = await getData(payload, 'Vehicle Alerts V2');
			if (isStalePayload(date)) return res; // older run → ignore
			if (Array.isArray(res) && res.length > 0) { this.setVehicleAlertsV2(res); }
			else { this.setVehicleAlertsV2([]); }
			return res;
		},

		async getDistanceDrivenKm(date: any, rootState: any) {
			const r = await loadDayWise({
				date, rootState, label: 'Distance Driven Km',
				envUrl: process.env.REACT_APP_OVERVIEW_DISTANCE_DRIVEN_KM,
				extract: (res) => sumXY(res?.[0]?.data),
				needFullResponse: false,
			});
			if (r === 'stale') return null; // older run → ignore
			if (!r.perDay) {
				this.setDistanceDrivenKm(Array.isArray(r.full) && r.full.length ? r.full[0] : initialValues.distanceDrivenKm);
				return r.full;
			}
			const data = toXYData(r.perDay);
			this.setDistanceDrivenKm(data.length ? { ...(r.full?.[0] ?? {}), name: r.full?.[0]?.name ?? 'Distance Driven Km', data } : initialValues.distanceDrivenKm);
			return data;
		},

		async getVehicleTypeUtilization(_: void) {
			const payload = generateRestAPI(
				[],
				process.env.REACT_APP_OVERVIEW_VEHICLE_TYPE_UTILIZATION,
			);
			const res: any[] = await getData(payload, 'Vehicle Type Utilization');
			if (Array.isArray(res) && res.length > 0) {
				this.setVehicleTypeUtilization(res[0]?.vehicle_utilization ?? []);
			} else {
				this.setVehicleTypeUtilization([]);
			}
			return res;
		},

		async getFuelConsumptionChart(date: any, rootState: any) {
			const r = await loadDayWise({
				date, rootState, label: 'Fuel Consumption Chart',
				envUrl: process.env.REACT_APP_OVERVIEW_FUEL_CONSUMPTION_CHART,
				extract: (res) => sumPairs(res?.[0]?.data),
				needFullResponse: false,
			});
			if (r === 'stale') return null; // older run → ignore
			if (!r.perDay) {
				this.setFuelConsumptionChart(Array.isArray(r.full) && r.full.length ? r.full[0] : null);
				return r.full;
			}
			const data = toPairData(r.perDay);
			this.setFuelConsumptionChart(data.length ? { ...(r.full?.[0] ?? {}), name: r.full?.[0]?.name ?? 'Fuel Consumption', data } : null);
			return data;
		},

		async getIdleFuelWasteChart(date: any, rootState: any) {
			const r = await loadDayWise({
				date, rootState, label: 'Idle Fuel Waste Chart',
				envUrl: process.env.REACT_APP_OVERVIEW_IDLE_FUEL_WASTE,
				extract: (res) => sumPairs(res?.[0]?.data),
				needFullResponse: false,
			});
			if (r === 'stale') return null; // older run → ignore
			if (!r.perDay) {
				this.setIdleFuelWasteChart(Array.isArray(r.full) && r.full.length ? r.full[0] : null);
				return r.full;
			}
			const data = toPairData(r.perDay);
			this.setIdleFuelWasteChart(data.length ? { ...(r.full?.[0] ?? {}), name: r.full?.[0]?.name ?? 'Idle Fuel Waste', data } : null);
			return data;
		},

		async getDriverScores(date: any, rootState: any) {
			const payload = generateRestAPI(
				buildDateParams(date, rootState),
				process.env.REACT_APP_OVERVIEW_DRIVER_SCORE,
			);
			const res: any[] = await getData(payload, 'Driver Scores');
			if (isStalePayload(date)) return res; // older run → ignore
			if (Array.isArray(res) && res.length > 0) { this.setDriverScores(res); }
			else { this.setDriverScores([]); }
			return res;
		},

		async getEcoDriverScore(date: any, rootState: any) {
			const payload = generateRestAPI(
				buildDateParams(date, rootState),
				process.env.REACT_APP_OVERVIEW_ECO_DRIVER_SCORE,
			);
			const res: any[] = await getData(payload, 'Eco Driver Score');
			if (isStalePayload(date)) return res; // older run → ignore
			if (Array.isArray(res) && res.length > 0) { this.setEcoDriverScore(res[0]); }
			else { this.setEcoDriverScore(null); }
			return res;
		},

		async getDriverNameFilter(_: void, rootState: any) {
			const fleetId = rootState?.auth?.customProperties?.fleetId ?? 'All Fleets';
			const payload = generateRestAPI(
				[
					{ fleet_id: fleetId },
					{ vin: 'All Vins' },
				],
				process.env.REACT_APP_OVERVIEW_DRIVER_NAME_FILTER,
			);
			const res: any[] = await getData(payload, 'Driver Name Filter');
			if (Array.isArray(res) && res.length > 0) {
				this.setDriverNameFilter(res);
			} else {
				this.setDriverNameFilter([]);
			}
			return res;
		},

		async getDriveVsIdle(payload: any, rootState: any) {
			const { startDate, endDate, driverName } = payload;
			const isFiltered = driverName && driverName !== 'All Drivers';
			const params: any[] = [
				...buildDateParams({ startDate, endDate }, rootState),
				{ driver_name: isFiltered ? driverName : 'All Drivers' },
			];
			const p = generateRestAPI(params, process.env.REACT_APP_OVERVIEW_DRIVE_VS_IDLE);
			const res: any[] = await getData(p, 'Drive vs Idle');
			if (isStalePayload(payload)) return res; // older run → ignore
			if (Array.isArray(res) && res.length > 0) {
				this.setDriveVsIdle(res);
				if (!isFiltered) {
					this.setDriveVsIdleAllDrivers(res);
				}
			} else {
				this.setDriveVsIdle([]);
				if (!isFiltered) {
					this.setDriveVsIdleAllDrivers([]);
				}
			}
			return res;
		},

		async getObdAlertEvents(date: any, rootState: any) {
			const r = await loadDayWise({
				date, rootState, label: 'OBD Alert Events',
				envUrl: process.env.REACT_APP_OVERVIEW_OBD_ALERT_EVENTS,
				extract: sumSeries,
				needFullResponse: false,
			});
			if (r === 'stale') return null; // older run → ignore
			const data = r.perDay ? toSeriesData(r.perDay) : (Array.isArray(r.full) ? r.full : []);
			this.setObdAlertEvents(data);
			return data;
		},

		async getDashcamAlertEvents(date: any, rootState: any) {
			const r = await loadDayWise({
				date, rootState, label: 'Dashcam Alert Events',
				envUrl: process.env.REACT_APP_OVERVIEW_DASHCAM_ALERT_EVENTS,
				extract: sumSeries,
				needFullResponse: false,
			});
			if (r === 'stale') return null; // older run → ignore
			const data = r.perDay ? toSeriesData(r.perDay) : (Array.isArray(r.full) ? r.full : []);
			this.setDashcamAlertEvents(data);
			return data;
		},

		async getDashcamAlertSeverity(date: any, rootState: any) {
			const payload = generateRestAPI(
				buildDateParams(date, rootState),
				process.env.REACT_APP_OVERVIEW_DASHCAM_ALERT_SEVERITY,
			);
			const res: any[] = await getData(payload, 'Dashcam Alert Severity');
			if (isStalePayload(date)) return res; // older run → ignore
			if (Array.isArray(res) && res.length > 0) { this.setDashcamAlertSeverity(res); }
			else { this.setDashcamAlertSeverity([]); }
			return res;
		},

		async getMaintenanceStatusV2(_: void) {
			const payload = generateRestAPI([],
				process.env.REACT_APP_OVERVIEW_MAINTENANCE_STATUS_V2,
			);
			const res: any[] = await getData(payload, 'Maintenance Status V2');
			if (Array.isArray(res) && res.length > 0) { this.setMaintenanceStatusV2(res[0]); }
			else { this.setMaintenanceStatusV2(null); }
			return res;
		},

		async getMaintServiceOverdue(_date: any) {
			// This endpoint is not date-filtered (dates intentionally not sent)
			const payload = generateRestAPI(
				[],
				process.env.REACT_APP_OVERVIEW_MAINT_SERVICE_OVERDUE,
			);
			const res: any[] = await getData(payload, 'Maint Service Overdue');
			if (Array.isArray(res) && res.length > 0) { this.setMaintServiceOverdue(res[0]); }
			else { this.setMaintServiceOverdue(null); }
			return res;
		},
	}),
});